/**
 * Como dizer o vencimento calculado pelo sistema.
 *
 * - vencido não diz "vence" ("Venceu em 24/08");
 * - no dia: "Vence hoje"; no seguinte: "Vence amanhã";
 * - na semana em curso, o dia da semana ("Vence qui., 22/10") — fora dela o
 *   dia da semana só faz o olho conferir uma informação que não ajuda;
 * - estimado (faltou o lugar completo, feriados locais podem não ter entrado):
 *   "~" antes da data, e o rótulo longo diz "estimado".
 *
 * Porte das regras do branch do Rubens (`d6648aac`, `1dc4fd39`).
 */
import { parseLocalDate } from '@/lib/formatters';

export type TomDoVencimento = 'vencido' | 'hoje' | 'proximo' | 'adiante';

export interface RotuloDoVencimento {
  /** "Vence em ~24/08" — curto, para a lista. */
  curto: string;
  /** "Vence em ~24/08/2026 (estimado)" — para o detalhe. */
  longo: string;
  tom: TomDoVencimento;
}

const DIAS_DA_SEMANA = ['dom.', 'seg.', 'ter.', 'qua.', 'qui.', 'sex.', 'sáb.'];
const UM_DIA = 86_400_000;

function semHora(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function diaMes(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Segunda a domingo — a semana do calendário da master (lib/semana). */
function mesmaSemana(a: Date, b: Date): boolean {
  const inicio = (d: Date) => {
    const s = semHora(d);
    s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
    return s.getTime();
  };
  return inicio(a) === inicio(b);
}

export function rotuloDoVencimento(vencimentoIso: string, estimado: boolean, hoje: Date = new Date()): RotuloDoVencimento {
  const venc = parseLocalDate(vencimentoIso);
  const dias = Math.round((semHora(venc).getTime() - semHora(hoje).getTime()) / UM_DIA);
  const til = estimado ? '~' : '';
  const sufixo = estimado ? ' (estimado)' : '';
  const ano = `/${venc.getFullYear()}`;

  if (dias < 0) {
    return { curto: `Venceu em ${til}${diaMes(venc)}`, longo: `Venceu em ${til}${diaMes(venc)}${ano}${sufixo}`, tom: 'vencido' };
  }
  if (dias === 0) {
    return { curto: `Vence hoje${estimado ? ' (~)' : ''}`, longo: `Vence hoje${sufixo}`, tom: 'hoje' };
  }
  if (dias === 1) {
    return { curto: `Vence amanhã${estimado ? ' (~)' : ''}`, longo: `Vence amanhã, ${diaMes(venc)}${sufixo}`, tom: 'proximo' };
  }
  if (mesmaSemana(venc, hoje)) {
    const dia = `${DIAS_DA_SEMANA[venc.getDay()]}, ${til}${diaMes(venc)}`;
    return { curto: `Vence ${dia}`, longo: `Vence ${dia}${sufixo}`, tom: 'proximo' };
  }
  return { curto: `Vence em ${til}${diaMes(venc)}`, longo: `Vence em ${til}${diaMes(venc)}${ano}${sufixo}`, tom: 'adiante' };
}
